import { useEffect, useState } from 'react';
import ArrowDown from '@tradera/blueprint/theme/icons2/arrow-down.svg?jsx';
import ArrowRight from '@tradera/blueprint/theme/icons2/arrow-right.svg?jsx';
import {
    Button,
    type ButtonProps,
} from 'tradera/components/atoms/button/button';
import styles from './accordion.module.scss';
import { classNames } from 'tradera/utils/class-names';
import { AccordionNextLevel } from './accordion-next-level';

type AccordionProps = React.PropsWithChildren<{
    title?: string;
    className?: string;
    skipBorderOnLast?: boolean;
    bgColor?: string;
}>;

export const Accordion = ({
    children,
    title,
    skipBorderOnLast,
    className,
    bgColor = 'bg-white',
}: AccordionProps) => (
    <div
        className={classNames(
            bgColor,
            styles.accordion,
            skipBorderOnLast && styles.skipBorderOnLast,
            className,
        )}>
        {title && (
            <div className="d-md-none header text-center font-weight-bold">
                {title}
            </div>
        )}
        {children}
    </div>
);

type AccordionItemProps = React.PropsWithChildren<{
    hasNextLevel?: boolean;
    nextLevel?: { className?: string; bodyClassName?: string };
    className?: string;
    /**
     * Defaults to true
     */
    collapsable?: boolean;
    hideTitle?: boolean;
    defaultOpen?: boolean;
    forceOpen?: boolean;
    onChange?: (open: boolean) => void;
    title?: string | React.ReactNode;
    label?: React.ReactNode;
    onDismiss?: () => void;
    showLabelWhileOpen?: boolean;
    borderBottom?: boolean;
    buttonNoPaddingTop?: boolean;
    buttonNoPaddingBottom?: boolean;
}> &
    Pick<ButtonProps, 'nonBrandedText'>;

export const AccordionItem = ({
    className = '',
    collapsable = true,
    hideTitle = false,
    defaultOpen,
    forceOpen,
    label,
    onChange,
    children,
    title,
    hasNextLevel,
    nextLevel,
    borderBottom = true,
    onDismiss,
    showLabelWhileOpen = false,
    /* inherited from ButtonProps */
    nonBrandedText,
    /* end inherited from ButtonProps */
    buttonNoPaddingTop = false,
    buttonNoPaddingBottom = false,
}: AccordionItemProps) => {
    const [accordionOpen, setAccordionOpen] = useState(defaultOpen);

    useEffect(() => {
        if (forceOpen) {
            setAccordionOpen(true);
        }
    }, [forceOpen]);

    return (
        <div
            className={classNames(
                borderBottom && styles.accordionItem,
                className,
            )}>
            {hideTitle ? (
                <div className={styles.itemBody}>{children}</div>
            ) : (
                <>
                    <Button
                        type="button"
                        theme="unbutton"
                        className={classNames(
                            'd-flex align-items-center text-left w-100 pointer-events',
                            { 'cursor-pointer': collapsable },
                            buttonNoPaddingTop ? 'pt-0' : 'pt-2',
                            buttonNoPaddingBottom ? 'pb-0' : 'pb-2',
                        )}
                        onClick={
                            collapsable
                                ? () => {
                                      setAccordionOpen(!accordionOpen);
                                      onChange?.(!accordionOpen);
                                  }
                                : () => {}
                        }
                        brandedText={!nonBrandedText}>
                        <span className="flex-grow-1 flex-shrink-0">
                            {title}
                        </span>
                        {label && (!accordionOpen || showLabelWhileOpen) ? (
                            <span className="ml-auto d-block text-nowrap text-truncate text-right pl-1 font-weight-normal font-inter">
                                {label}
                            </span>
                        ) : null}

                        <span>
                            {collapsable && (
                                <span
                                    className={classNames('flex-grow-1 pl-1', {
                                        [styles.arrowOpen]:
                                            accordionOpen && !hasNextLevel,
                                    })}>
                                    {hasNextLevel ? (
                                        <ArrowRight className="icon right-arrow icon-sm icon-dark" />
                                    ) : (
                                        <ArrowDown className="icon icon-sm icon-dark" />
                                    )}
                                </span>
                            )}
                        </span>
                    </Button>
                    {accordionOpen && (
                        <>
                            {hasNextLevel ? (
                                <AccordionNextLevel
                                    onDismiss={() => setAccordionOpen(false)}
                                    onClose={onDismiss}
                                    title={title}
                                    classNames={nextLevel}>
                                    {children}
                                </AccordionNextLevel>
                            ) : (
                                <div
                                    className={classNames(styles.itemBody, {
                                        [styles.collapsed]: !accordionOpen,
                                    })}>
                                    {children}
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
};
